import { m } from 'framer-motion';
import { useState, useCallback, useEffect, useContext } from 'react';
// @mui
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Stack from '@mui/material/Stack';
import Badge from '@mui/material/Badge';
import Drawer from '@mui/material/Drawer';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
import { useResponsive } from 'src/hooks/use-responsive';
// _mock
// components
import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import { varHover } from 'src/components/animate';
//
import NotificationItem from './notification-item';
import { LoadingScreen } from 'src/components/loading-screen';
import { useSnackbar } from 'notistack';
import { NotificationContext } from 'src/contexts/notification/notificationContext';
import { useRef } from 'react';
// ----------------------------------------------------------------------

const TABS = [
  {
    value: 'All',
    label: 'All',
    count: 22,
  },
  {
    value: 'Vendors',
    label: 'Vendors',
    count: 12,
  },
  {
    value: 'Customers',
    label: 'Customers',
    count: 10,
  },
  {
    value: 'Reportings',
    label: 'Reportings',
    count: 3,
  },
  {
    value: 'Chat/Call',
    label: 'Chat/Call',
    count: 12,
  },
];

// ----------------------------------------------------------------------

export default function NotificationsPopover() {
  const drawer = useBoolean();

  const smUp = useResponsive('up', 'sm');

  const listRef = useRef(null);
  const scrollLockRef = useRef(false);

  const [currentTab, setCurrentTab] = useState('All');

  const handleChangeTab = useCallback((event, newValue) => {
    setCurrentTab(newValue);
  }, []);

  const [notificationsUnreadCount, setNotificationsUnreadCount] = useState(0);

  const [activePage, setActivePage] = useState(0);

    const {
    notifications,
    unreadCount,
    isLoadingNotifications,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    notificationMeta,
    deleteNotificationById
  } = useContext(NotificationContext);

  const viewAllNotifications = useCallback(async () => {
    setActivePage(0);
    drawer.onTrue();

    fetchNotifications({page: 0, size: 10});
  }, [drawer, fetchNotifications]);

  const handleMarkAllAsRead = useCallback(() => {
    markAllAsRead();
  }, [markAllAsRead]);

  const actionMarkNotificationAsRead = useCallback((notificationID) => {
    markAsRead(notificationID);
  }, [markAsRead]);

  const handleScroll = useCallback((e) => {
    const el = e.target;
    if (scrollLockRef.current) return;

    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 10) {
      if (activePage < notificationMeta.totalPages - 1) {
        scrollLockRef.current = true;
        
        const nextPage = activePage + 1;
        setActivePage(nextPage);

        fetchNotifications({ page: nextPage, size: 5, isLoadMore: true });

        setTimeout(() => {
          scrollLockRef.current = false;
        }, 1000);
      }
    }
  }, [activePage, notificationMeta.totalPages, fetchNotifications]);

  const renderHead = (
    <Stack direction="row" alignItems="center" sx={{ py: 2, pl: 2.5, pr: 1, minHeight: 68 }}>
      <Typography variant="h6" sx={{ flexGrow: 1 }}>
        Notifications
      </Typography>

      <Tooltip title="Mark all as read">
        <IconButton 
          color="primary" 
          onClick={handleMarkAllAsRead} 
        >
          <Iconify icon="eva:done-all-fill" />
        </IconButton>
      </Tooltip>

      {!smUp && (
        <IconButton onClick={drawer.onFalse}>
          <Iconify icon="mingcute:close-line" />
        </IconButton>
      )}
    </Stack>
  );

  const renderTabs = (
    <Tabs 
      value={currentTab} 
      onChange={handleChangeTab}
    >
      {TABS.map((tab) => (
        <Tab
          key={tab.value}
          iconPosition="end"
          value={tab.value}
          label={tab.label}
          icon={
            <Label
              variant={((tab.value === 'All' || tab.value === currentTab) && 'filled') || 'soft'}
              color={
                (tab.value === 'All' && 'info') ||
                (tab.value === 'Vendors' && 'success') ||
                (tab.value === 'Customers' && 'warning') ||
                (tab.value === 'Bookings' && 'error') ||
                (tab.value === 'Reportings' && 'default') ||
                (tab.value === 'Chat/Call' && 'primary') ||
                'default'
              }
            >
              {(() => {
                const unreadList = notifications.filter((n) => !n.isRead);
                
                if (tab.value === 'All') return unreadList.length;
                if (tab.value === 'Reportings') return unreadList.filter(n => ["REPORT_VEHICLE_SUBMITTED", "REPORT_CUSTOMER_SUBMITTED", "REPORT_VENDOR_SUBMITTED"].includes(n.type)).length;
                if (tab.value === 'Chat/Call') return unreadList.filter(n => n.type === "CHAT_MESSAGE").length;
                if (tab.value === 'Vendors') return unreadList.filter(n => ["PROFILE_DOCS_SUBMITTED", "PROFILE_DOCS_REJECTED"].includes(n.type) && n.senderRole === 'VENDOR').length;
                if (tab.value === 'Customers') return unreadList.filter(n => ["PROFILE_DOCS_SUBMITTED", "PROFILE_DOCS_REJECTED"].includes(n.type) && n.senderRole === 'CUSTOMER').length;
                
                return 0; // unmatched
              })()}
            </Label>
          }
          sx={{
            '&:not(:last-of-type)': {
              mr: 3,
            },
          }}
        />
      ))}
    </Tabs>
  );

  const filteredNotifications = notifications.filter((notification) => {
    if (currentTab === 'All') return true;
    
    if (currentTab === 'Reportings') {
      return ["REPORT_VEHICLE_SUBMITTED", "REPORT_CUSTOMER_SUBMITTED", "REPORT_VENDOR_SUBMITTED"].includes(notification.type);
    }
    
    if (currentTab === 'Chat/Call') {
      return notification.type === "CHAT_MESSAGE";
    }

    if (currentTab === 'Vendors') {
      if (["PROFILE_DOCS_SUBMITTED", "PROFILE_DOCS_REJECTED"].includes(notification.type)) {
        return notification.senderRole === 'VENDOR';
      }
      return false;
    }

    if (currentTab === 'Customers') {
      if (["PROFILE_DOCS_SUBMITTED", "PROFILE_DOCS_REJECTED"].includes(notification.type)) {
        return notification.senderRole === 'CUSTOMER';
      }
      return false;
    }

    return false; 
  });

  let body;

  if (isLoadingNotifications) {
    body = (
      <Box my={15}>
        <LoadingScreen />
      </Box>
    );
  } else if (filteredNotifications.length === 0) {
    body = (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">No notifications found.</Typography>
      </Box>
    );
  } else {
    body = filteredNotifications.map((notification) => (
      <NotificationItem 
        key={notification.id} 
        notification={notification} 
        markNotificationAsRead={actionMarkNotificationAsRead}
        deleteNotification={deleteNotificationById}
      />
    ));
  }

  const renderList = (
    <Stack
      ref={listRef}
      sx={{
        overflowY: 'auto',
        flexGrow: 1,
      }}
      className="hide-scrollbar"
      onScroll={handleScroll}
    >
      {body}
    </Stack>
  );

  return (
    <>
      <IconButton
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        color={drawer.value ? 'primary' : 'default'}
        onClick={viewAllNotifications}
      >
        <Badge badgeContent={unreadCount} color="error">
          <Iconify icon="solar:bell-bing-bold-duotone" width={24} />
        </Badge>
      </IconButton>

      <Drawer
        open={drawer.value}
        onClose={drawer.onFalse}
        anchor="right"
        slotProps={{
          backdrop: { invisible: true },
        }}
        PaperProps={{
          sx: { 
            width: 1, 
            maxWidth: 420
          },
        }}
      >
        {renderHead}

        {renderTabs}

        <Divider />

        {renderList}
      </Drawer>
    </>
  );
}
